/**
 * Kontroll's record list (V7.5.6, #275): selection column, Ansatt, Dato, Planlagt, Registrert, Arbeidstid,
 * Status, the row action; the selection bar above it and the pagination under it, in the prototype's words
 * and layout (DIFF D058 to D097, D168, D172). The status is plain coloured text with an icon, no second line;
 * the hours are amber only at exactly 0 t. On a phone each record is a card: name across with the box at the
 * top right, the date under the name, Planlagt and Registrert side by side, an «Arbeidstid» line, then the
 * status with «Vurder» on one line. A day split on a project switch stays one day line (#115).
 */
import type { ApiShift } from '@/api/data';
import { T } from '@/i18n';
import { plannedPaidHours, worked, workedResult } from '@/rules';
import { PersonLabel } from '@/ui/PersonLabel';
import { num } from '@/ui/format';
import { Icon } from '@/ui/icons';
import type { BreakPolicy } from '@timefly/core';
import { useMemo, useState } from 'react';
import { dateShort, hoursText, interval } from './kfmt';
import type { Rec } from './kontroll';
import { splitParts } from './review';

export const PAGE = 25;
const dateOf = (s: ApiShift) => s.start_time;

/** the status as the prototype draws it: icon and words, the tone by state */
export function statusOf(r: Rec): {
  tone: 'needs' | 'ready' | 'approved' | 'ongoing' | 'waiting' | 'planned';
  icon: 'warn' | 'clock' | 'check';
  text: string;
} {
  if (r.bucket === 'approved') return { tone: 'approved', icon: 'check', text: T('Godkjent') };
  if (r.bucket === 'ongoing') return { tone: 'ongoing', icon: 'clock', text: T('Pågår') };
  if (r.bucket === 'planned') return { tone: 'planned', icon: 'clock', text: T('Planlagt') };
  if (r.bucket === 'accounted') return { tone: 'planned', icon: 'check', text: T('Dekket av fravær') };
  if (r.exceptions.includes('waiting'))
    return { tone: 'waiting', icon: 'clock', text: T('Venter på ansatt') };
  if (r.bucket === 'ready')
    return {
      tone: 'ready',
      icon: 'clock',
      text: r.corrected ? T('Rettet, klar') : T('Klar til godkjenning'),
    };
  return { tone: 'needs', icon: 'warn', text: T(r.problem.label) };
}

export function Status({ r }: { r: Rec }) {
  const st = statusOf(r);
  return (
    <span className={`kst ${st.tone}`}>
      <Icon name={st.icon} size={14} />
      {st.text}
    </span>
  );
}

/** «Arbeidstid ikke avklart» for a record without both clock times; else the break taken off */
export function recordedNote(s: ApiShift, policy: BreakPolicy | null): string {
  if (!s.punch_in_time || !s.punch_out_time) return T('Arbeidstid ikke avklart');
  const r = workedResult(s, policy);
  return r.status === 'ok' ? T('{n} min pausetrekk', { n: Math.round(r.deductedMinutes) }) : '';
}

export function plannedText(
  s: ApiShift,
  policy: BreakPolicy | null,
): { time: string; days: string | null; note: string } {
  if (/uten plan/i.test(s.shiftType?.name ?? '')) return { time: T('Ingen plan'), days: null, note: '' };
  const mins = Math.round((Date.parse(s.end_time) - Date.parse(s.start_time)) / 60000);
  const brk = policy ? Math.max(0, Math.round(mins - plannedPaidHours(s, policy) * 60)) : 0;
  const iv = interval(s.start_time, s.end_time);
  return { ...iv, note: T('{n} min pause', { n: brk }) };
}

export function KontrollQueue({
  recs,
  total,
  page,
  onPage,
  policy,
  canPick,
  picked,
  setPicked,
  openId,
  onOpen,
  nameOf,
  subOf,
  project,
  caption,
  onBatch,
  busy,
}: {
  /** every record of the list, in order; the page is cut here */
  recs: Rec[];
  total: number;
  page: number;
  onPage: (p: number) => void;
  policy: BreakPolicy | null;
  /** the selection column: on «Klare til godkjenning» for whoever may approve, on a fresh read; else no column */
  canPick: boolean;
  picked: Set<number>;
  setPicked: (n: Set<number>) => void;
  openId: number | null;
  onOpen: (id: number) => void;
  nameOf: (s: ApiShift) => string;
  subOf: (uid: number | null) => string;
  /** the project column when projects are on: its heading and a record's project */
  project?: { heading: string; of: (s: ApiShift) => string; lower: string } | undefined;
  caption: string;
  onBatch: (ids: number[]) => void;
  busy: boolean;
}) {
  const pages = Math.max(1, Math.ceil(total / PAGE));
  const pg = Math.min(page, pages - 1);
  const pageRecs = recs.slice(pg * PAGE, pg * PAGE + PAGE);
  // owner 2026-11-01: the selection column is on «Klare til godkjenning» only, every box live, never a greyed one
  const pickable = (r: Rec) => canPick && r.bucket === 'ready' && !r.exceptions.includes('waiting');
  const pickOnPage = pageRecs.filter(pickable);
  const allPicked = pickOnPage.length > 0 && pickOnPage.every((r) => picked.has(r.s.id));
  const somePicked = pickOnPage.some((r) => picked.has(r.s.id));
  const chosen = recs.filter((r) => picked.has(r.s.id) && pickable(r));
  // a day split on a project switch (#115): one day line that opens to its parts
  const splitDays = useMemo(() => {
    const m = new Map<string, ApiShift[]>();
    for (const r of pageRecs) {
      if (!r.s.punch_in_time) continue;
      const k = `${r.s.user_id}:${dateOf(r.s).slice(0, 10)}`;
      m.set(k, [...(m.get(k) ?? []), r.s]);
    }
    for (const [k, v] of m) {
      const parts = splitParts(v);
      if (parts) m.set(k, parts);
      else m.delete(k);
    }
    return m;
  }, [pageRecs]);
  const [openDays, setOpenDays] = useState<Set<string>>(new Set());

  const row = (r: Rec) => {
    const s = r.s;
    const h = r.bucket === 'planned' || r.bucket === 'ongoing' ? null : worked(s, policy);
    const plan = plannedText(s, policy);
    const reg = interval(s.punch_in_time, s.punch_out_time);
    const sel = picked.has(s.id) && pickable(r);
    const action = r.bucket === 'approved' ? T('Se detaljer') : T('Vurder');
    return (
      <tr key={s.id} className={openId === s.id ? 'open' : ''} data-testid="review-row">
        {canPick && (
          <td className="kcb">
            {pickable(r) && (
              <label className="kcbx">
                <input
                  type="checkbox"
                  aria-label={T('Velg {name}, {date}', { name: nameOf(s), date: dateShort(s.start_time) })}
                  checked={sel}
                  onChange={() => {
                    const n = new Set(picked);
                    sel ? n.delete(s.id) : n.add(s.id);
                    setPicked(n);
                  }}
                />
              </label>
            )}
          </td>
        )}
        <td className="kwhocell">
          <PersonLabel id={s.user_id ?? 0} name={nameOf(s)}>
            <b>{nameOf(s)}</b>
            <small>{subOf(s.user_id) || s.shiftType?.name || ''}</small>
          </PersonLabel>
        </td>
        <td className="kdate" data-k={T('Dato')}>
          {dateShort(s.start_time)}
        </td>
        <td className="kplan" data-k={T('Planlagt')}>
          <span className="ktime">{plan.time}</span>
          {plan.days && <small>{plan.days}</small>}
          {plan.note && <small>{plan.note}</small>}
        </td>
        <td className="kreg" data-k={T('Registrert')}>
          <span className="ktime">{reg.time}</span>
          {reg.days && <small>{reg.days}</small>}
          <small>{recordedNote(s, policy)}</small>
        </td>
        <td className={`num khours${h === 0 ? ' zero' : ''}`} data-k={T('Arbeidstid')}>
          {h == null ? <span aria-label={T('ukjent')}>–</span> : hoursText(h)}
        </td>
        <td className="kstatus">
          <Status r={r} />
        </td>
        {project && (
          <td className="kproj" data-k={project.heading}>
            {project.of(s)}
          </td>
        )}
        <td className="tlact">
          <button
            type="button"
            className="btn ghost kact"
            onClick={() => onOpen(s.id)}
            data-review-open={s.id}
            aria-label={`${action}: ${nameOf(s)}, ${dateShort(s.start_time)}`}
          >
            {action}
          </button>
        </td>
      </tr>
    );
  };

  return (
    <>
      {chosen.length > 0 && (
        <section className="ksel" aria-label={T('Valgte registreringer')}>
          <strong aria-live="polite">
            {T('{n} valgt, {h} t', {
              n: chosen.length,
              h: num(chosen.reduce((a, r) => a + (worked(r.s, policy) ?? 0), 0)),
            })}
          </strong>
          <span className="sp" />
          <button type="button" className="btn ghost" onClick={() => setPicked(new Set())}>
            {T('Fjern valg')}
          </button>
          <button
            type="button"
            className="btn pri"
            disabled={busy}
            onClick={() => onBatch(chosen.map((r) => r.s.id))}
          >
            {T('Godkjenn valgte')}
          </button>
        </section>
      )}
      <div className="ktablewrap">
        <table className={`mt tlrev kqueue${canPick ? ' pick' : ''}`} data-testid="timer-review">
          <caption className="sr">{caption}</caption>
          <thead>
            <tr>
              {canPick && (
                <th scope="col" className="kcb">
                  <label className="kcbx">
                    <input
                      type="checkbox"
                      aria-label={T('Velg alle')}
                      checked={allPicked}
                      ref={(el) => {
                        if (el) el.indeterminate = somePicked && !allPicked;
                      }}
                      onChange={() => {
                        const n = new Set(picked);
                        for (const r of pickOnPage) allPicked ? n.delete(r.s.id) : n.add(r.s.id);
                        setPicked(n);
                      }}
                    />
                  </label>
                </th>
              )}
              <th scope="col" className="kwhocell">
                {T('Ansatt')}
              </th>
              <th scope="col" className="kdate">
                {T('Dato')}
              </th>
              <th scope="col">{T('Planlagt')}</th>
              <th scope="col">{T('Registrert')}</th>
              <th scope="col" className="num khours">
                {T('Arbeidstid')}
              </th>
              <th scope="col">{T('Status')}</th>
              {project && <th scope="col">{project.heading}</th>}
              <th scope="col" className="tlact">
                <span className="sr">{T('Handling')}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {pageRecs.flatMap((r) => {
              const s = r.s;
              const key = `${s.user_id}:${dateOf(s).slice(0, 10)}`;
              const group = splitDays.get(key);
              if (!group?.includes(s)) return [row(r)];
              const open = openDays.has(key);
              if (group[0]?.id !== s.id) return open ? [row(r)] : [];
              const total = group.map((x) => worked(x, policy)).filter((x): x is number => x != null);
              const reg = interval(group[0]?.punch_in_time, group[group.length - 1]?.punch_out_time);
              return [
                <tr key={`day-${key}`} className="tlday" data-testid="review-day">
                  {canPick && <td className="kcb" />}
                  <td className="kwhocell">
                    <PersonLabel id={s.user_id ?? 0} name={nameOf(s)}>
                      <b>{nameOf(s)}</b>
                      <small>
                        {T('{n} deler, byttet {what}', {
                          n: group.length,
                          what: project?.lower ?? T('prosjekt'),
                        })}
                      </small>
                    </PersonLabel>
                  </td>
                  <td className="kdate" data-k={T('Dato')}>
                    {dateShort(s.start_time)}
                  </td>
                  <td className="kplan" data-k={T('Planlagt')}>
                    <span className="ktime">{plannedText(group[0] as ApiShift, policy).time}</span>
                  </td>
                  <td className="kreg" data-k={T('Registrert')}>
                    <span className="ktime">{reg.time}</span>
                  </td>
                  <td className="num khours" data-k={T('Arbeidstid')}>
                    {total.length ? `${num(total.reduce((a, x) => a + x, 0))} t` : '–'}
                  </td>
                  <td className="kstatus">
                    <span className={`kst ${group.every((x) => x.is_approved) ? 'approved' : 'ready'}`}>
                      {group.every((x) => x.is_approved)
                        ? T('Godkjent')
                        : T('{n} deler', { n: group.length })}
                    </span>
                  </td>
                  {project && (
                    <td className="kproj" data-k={project.heading}>
                      {[...new Set(group.map(project.of))].join(' → ')}
                    </td>
                  )}
                  <td className="tlact">
                    <button
                      type="button"
                      className="btn ghost kact"
                      aria-expanded={open}
                      onClick={() =>
                        setOpenDays((o) => {
                          const n = new Set(o);
                          open ? n.delete(key) : n.add(key);
                          return n;
                        })
                      }
                    >
                      {open ? T('Skjul delene') : T('Vis {n} deler', { n: group.length })}
                    </button>
                  </td>
                </tr>,
                ...(open ? [row(r)] : []),
              ];
            })}
          </tbody>
        </table>
      </div>
      <Pagination count={total} page={pg} size={PAGE} onPage={onPage} />
    </>
  );
}

/** «Viser 1–25 av 65» on the left; «‹ Forrige», «Side 1 av 3», «Neste ›» on the right (D091, D093) */
export function Pagination({
  count,
  page,
  size,
  onPage,
}: {
  count: number;
  page: number;
  size: number;
  onPage: (p: number) => void;
}) {
  if (!count) return null;
  const pages = Math.max(1, Math.ceil(count / size));
  return (
    <nav className="kpag" aria-label={T('Sider')}>
      <span>
        {T('Viser {a}–{b} av {n}', { a: page * size + 1, b: Math.min((page + 1) * size, count), n: count })}
      </span>
      <div>
        <button type="button" className="btn ghost" disabled={page === 0} onClick={() => onPage(page - 1)}>
          <Icon name="chevronLeft" size={16} />
          {T('Forrige')}
        </button>
        <span>{T('Side {a} av {b}', { a: page + 1, b: pages })}</span>
        <button
          type="button"
          className="btn ghost"
          disabled={page + 1 >= pages}
          onClick={() => onPage(page + 1)}
        >
          {T('Neste')}
          <Icon name="chevronRight" size={16} />
        </button>
      </div>
    </nav>
  );
}
